// The plans behind the widgets' sizes: how big each part of a widget is, in
// em, and which of its layouts it takes in a tile of a given size. Kept apart
// from the widgets, which need a browser, so tests can check every size of
// every widget without one. The numbers here are measured from the styles in
// widgets.css; change one and the other follows.

import { biggest, fitType, gridBox, pickLayout, type Box, type Layout } from "./layout";
import { dayKey } from "../../shared/calendar";

export { dayKey }; // a habit log's day key is the calendar's

// ---------------------------------------------------------------------------
// Clock

export function partsIn(d: Date, tz?: string) {
  try {
    const f = new Intl.DateTimeFormat("en-US", { timeZone: tz || undefined, hour: "numeric", minute: "numeric", second: "numeric", hourCycle: "h23", weekday: "long", month: "long", day: "numeric" });
    const o: Record<string, string> = {};
    for (const p of f.formatToParts(d)) o[p.type] = p.value;
    return { h: Number(o.hour), m: Number(o.minute), s: Number(o.second), weekday: o.weekday, month: o.month, day: o.day };
  } catch {
    return { h: d.getHours(), m: d.getMinutes(), s: d.getSeconds(), weekday: "", month: "", day: "" };
  }
}

const ZONE_OK = new Map<string, boolean>();
function knownZone(tz: string): boolean {
  let ok = ZONE_OK.get(tz);
  if (ok === undefined) {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: tz });
      ok = true;
    } catch {
      ok = false;
    }
    ZONE_OK.set(tz, ok);
  }
  return ok;
}

/** The places a clock shows: its list of places, or the one place it had, or none for this device's own time. */
export function clockZones(props: Record<string, unknown>): string[] {
  const list = Array.isArray(props.zones) ? props.zones.filter((z): z is string => typeof z === "string" && !!z && knownZone(z)) : [];
  const one = typeof props.tz === "string" && props.tz && knownZone(props.tz) ? [props.tz] : [];
  return [...new Set(list.length ? list : one)].slice(0, 8);
}

/** Hands or digits. A world clock saved before it joined the clock (type "worldclock") showed digits, so it keeps them. */
export function clockFace(type: string, props: Record<string, unknown>): "analog" | "digital" {
  if (props.face === "analog" || props.face === "digital") return props.face;
  return type === "worldclock" ? "digital" : "analog";
}

export function cityOf(tz: string): string {
  return tz.split("/").pop()!.replace(/_/g, " ");
}

/** How many minutes ahead of UTC a place's clocks are at d (this device's own with no place). */
export function utcOffset(d: Date, tz?: string): number {
  try {
    const f = new Intl.DateTimeFormat("en-US", { timeZone: tz || undefined, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" });
    const o: Record<string, number> = {};
    for (const p of f.formatToParts(d)) if (p.type !== "literal") o[p.type] = Number(p.value);
    const wall = Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute);
    return Math.round((wall - Math.floor(d.getTime() / 60_000) * 60_000) / 60_000);
  } catch {
    return -d.getTimezoneOffset();
  }
}

export interface Place {
  tz: string;
  city: string;
  h: number;
  m: number;
  s: number;
  /** "+4h", "−9h", "+5.5h", or "Here" when it keeps this device's time */
  diff: string;
  /** "Today", "Tomorrow" or "Yesterday", by this device's calendar */
  day: string;
  /** between 7 in the morning and 7 at night there */
  light: boolean;
  /** how far through its day it is, 0 to 1 */
  through: number;
}

export function placeAt(d: Date, tz: string): Place {
  const p = partsIn(d, tz);
  const off = utcOffset(d, tz) - utcOffset(d);
  const hours = Math.abs(off) / 60;
  const diff = off === 0 ? "Here" : `${off > 0 ? "+" : "−"}${hours % 1 ? hours.toFixed(1) : hours}h`;
  const there = new Date(d.getTime() + off * 60_000);
  const shift = Math.round((Date.UTC(there.getFullYear(), there.getMonth(), there.getDate()) - Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())) / 86_400_000);
  return { tz, city: cityOf(tz), h: p.h, m: p.m, s: p.s, diff, day: shift > 0 ? "Tomorrow" : shift < 0 ? "Yesterday" : "Today", light: p.h >= 7 && p.h < 19, through: (p.h * 60 + p.m) / 1440 };
}

// a clock's sizes in em, measured from the styles in widgets.css (Clock)
export const DIGITAL = { full: { w: 12, h: 5.6 }, bare: { w: 11.6, h: 3.8 } };
export const PLACE = {
  digital: { col: { w: 9.9, h: 5.9 }, colBare: { w: 9.9, h: 4.9 }, row: { w: 15.2, h: 3.4 }, rowBare: { w: 13.2, h: 3 } },
  analog: { col: { w: 7.9, h: 8.8 }, colBare: { w: 7.4, h: 7.6 }, row: { w: 15.4, h: 3.6 }, rowBare: { w: 13.4, h: 3.2 } },
};

/** A clock with one place, as digits: the time and its date, or the time alone in a tile too small for both. */
export function digitalFit(box: Box): { id: "full" | "bare"; px: number } {
  return pickLayout<"full" | "bare">(box, [{ id: "full", natural: DIGITAL.full, min: 11 }, { id: "bare", natural: DIGITAL.bare }], 34);
}

export type Arrange = "cols" | "stack" | "grid" | "grid3" | "rows";

/**
 * A clock with several places: side by side, stacked, in a grid or in rows,
 * whichever lets them be biggest. The small print (hours from here, the
 * day, the bar of the day) goes first when they get too small to read, then
 * the last places, which it counts: shown is how many fit.
 */
export function worldFit(box: Box, places: number, analog: boolean): { id: Arrange; px: number; bare: boolean; shown: number } {
  const cells = PLACE[analog ? "analog" : "digital"];
  const ways = (n: number, bare: boolean) =>
    (
      [
        n <= 4 && { id: "cols", natural: gridBox(n, n, bare ? cells.colBare : cells.col, 0.4) },
        n >= 2 && n <= 4 && { id: "stack", natural: gridBox(n, 1, bare ? cells.colBare : cells.col, 0.4) },
        n >= 3 && { id: "grid", natural: gridBox(n, 2, bare ? cells.colBare : cells.col, 0.4) },
        n >= 5 && { id: "grid3", natural: gridBox(n, 3, bare ? cells.colBare : cells.col, 0.4) },
        { id: "rows", natural: gridBox(n, 1, bare ? cells.rowBare : cells.row, 0) },
      ] as (false | Layout<Arrange>)[]
    ).filter((l): l is Layout<Arrange> => !!l);
  let shown = Math.max(1, places);
  // the cap only stops a huge tile drawing absurdly large type; the content fills the tile well before it
  let fit = biggest(box, ways(shown, false), 40);
  if (!(fit.px > 0 && fit.px < 11.5)) return { ...fit, bare: false, shown };
  fit = biggest(box, ways(shown, true), 40);
  while (shown > 1 && fit.px < 9.5) fit = biggest(box, ways(--shown, true), 40);
  return { ...fit, bare: true, shown };
}

// ---------------------------------------------------------------------------
// Countdown

// a countdown's sizes in em, measured from the styles in widgets.css (Countdown): a card for each unit, the
// line over them (what it counts to, and when), and the days alone
export const COUNT = { card: { w: 3.8, h: 4.2 }, digit: 1.2, gap: 0.45, head: 2.1, alone: { w: 7.4, h: 5.4 } };

export type CountFit = "row" | "grid" | "alone";

/** The cards in a row or in a grid, whichever is bigger, or the biggest unit alone in a tile too small for cards. */
export function countFit(box: Box, cards: number, digits: number): { id: CountFit; px: number } {
  const card = { w: COUNT.card.w + Math.max(0, digits - 2) * COUNT.digit, h: COUNT.card.h };
  const row = { w: cards * card.w + (cards - 1) * COUNT.gap, h: COUNT.head + card.h };
  const cols = cards === 4 ? 2 : cards;
  const lines = Math.ceil(cards / cols);
  const grid = { w: cols * card.w + (cols - 1) * COUNT.gap, h: COUNT.head + lines * card.h + (lines - 1) * COUNT.gap };
  const both = biggest<CountFit>(box, [{ id: "row", natural: row }, { id: "grid", natural: grid }], 44);
  return both.px >= 10.5 ? both : { id: "alone", px: fitType(box, { w: COUNT.alone.w + Math.max(0, digits - 2) * 1.6, h: COUNT.alone.h }, 40) };
}

// ---------------------------------------------------------------------------
// Weather

// the weather's sizes in em, measured from the styles in widgets.css (Weather): now (the temperature, the sky's
// words, the day's high and low), a day of the forecast in a strip or in a row, and the padding around them
export const WEATHER = { now: { w: 8.6, h: 5.3 }, day: { w: 3.5, h: 4.7 }, row: { w: 11.5, h: 2.35 }, gap: 1, pad: 1.1 };

export type WeatherFit = "side" | "strip" | "list" | "now";

/**
 * Now and the days to come: the days beside now in a wide tile, under it in a
 * strip, or as rows in a tall one, whichever lets it all be biggest, with as
 * many of the five days as read well; now alone in a small tile.
 */
export function weatherFit(box: Box, days: number): { id: WeatherFit; px: number; days: number } {
  const W = WEATHER;
  const p2 = 2 * W.pad;
  for (let n = Math.min(5, days); n >= 3; n--) {
    const best = biggest<WeatherFit>(
      box,
      [
        { id: "side", natural: { w: W.now.w + W.gap + n * W.day.w + p2, h: Math.max(W.now.h, W.day.h) + p2 } },
        { id: "strip", natural: { w: Math.max(W.now.w, n * W.day.w) + p2, h: W.now.h + 0.8 + W.day.h + p2 } },
        { id: "list", natural: { w: Math.max(W.now.w, W.row.w) + p2, h: W.now.h + 0.6 + n * W.row.h + p2 } },
      ],
      26,
    );
    if (best.px >= 11.5) return { ...best, days: n };
  }
  return { id: "now", px: fitType(box, { w: W.now.w + p2, h: W.now.h + p2 }, 30), days: 0 };
}

// ---------------------------------------------------------------------------
// Focus timer

// a timer's sizes in em, measured from the styles in widgets.css (Focus timer)
export const TIMER = {
  /** the ring beside the time and its buttons */
  side: { w: 21, h: 7.7 },
  /** the time inside the ring, its buttons under it */
  stack: { w: 12.2, h: 12 },
  /** the time inside the ring, one round button under it */
  ring: { w: 7.4, h: 10.1 },
  /** the ring and its time alone: a click on it starts and pauses */
  dot: { w: 6.2, h: 6.2 },
};
export type TimerFit = keyof typeof TIMER;

/** A focus timer's layout: the ring beside the time or the time inside the ring, whichever is bigger, or a smaller ring with less around it. */
export function timerFit(box: Box): { id: TimerFit; px: number } {
  const both = biggest<TimerFit>(box, [{ id: "side", natural: TIMER.side }, { id: "stack", natural: TIMER.stack }], 48);
  return both.px >= 11 ? both : pickLayout<TimerFit>(box, [{ id: "ring", natural: TIMER.ring, min: 10 }, { id: "dot", natural: TIMER.dot }], 30);
}

// ---------------------------------------------------------------------------
// AI usage

// AI usage's sizes in em, measured from the styles in widgets.css (AI usage): a ring and the gap to its words beside
// it, or the room its words take under it; the gaps between rings; an app's name over its rings; the line under them
// (another model's week); the space between two apps one over the other or side by side; and
// a letter of a ring's label and of its time, with a little room spare
export const USAGE = { ring: 3.9, gap: 0.8, under: 2.66, colGap: 1.4, rowGap: 0.7, head: 1.47, more: 1.56, between: 1.82, beside: 3.22, label: 0.5, sub: 0.42, spare: 0.2 };

export type UsageFit = { id: "rows" | "line" | "strip" | "cols" | "stack"; px: number };

/** An app's rings as AI usage draws them: each ring's label and when it starts again, and whether a line goes under them. */
export interface UsageApp {
  rings: { label: string; sub: string }[];
  more: boolean;
}

/** How wide a ring's words are, in em: the longest label or time of any ring, so every ring lines up. */
export function usageWords(rings: { label: string; sub: string }[]): number {
  return Math.max(4.5, ...rings.map((r) => Math.max(r.label.length * USAGE.label, r.sub.length * USAGE.sub))) + USAGE.spare;
}

/** Each arrangement's natural size in em, for the apps it shows. */
export function usageBoxes(apps: UsageApp[]): Record<UsageFit["id"], Box> {
  const U = USAGE;
  const list = apps.length ? apps : [{ rings: [], more: false }];
  const words = usageWords(list.flatMap((a) => a.rings));
  // a ring with its words beside it, or under it
  const side = { w: U.ring + U.gap + words, h: U.ring };
  const under = { w: Math.max(U.ring, words), h: U.ring + U.under };
  // an app with no rings yet says so in a line, given a ring's room
  const app = (cols: number, cell: Box, a: UsageApp): Box => {
    const n = Math.max(1, a.rings.length);
    const c = Math.min(cols, n);
    const r = Math.ceil(n / c);
    return { w: c * cell.w + (c - 1) * U.colGap, h: U.head + r * cell.h + (r - 1) * U.rowGap + (a.more ? U.more : 0) };
  };
  const over = (cols: number, cell: Box): Box => {
    const b = list.map((a) => app(cols, cell, a));
    return { w: Math.max(...b.map((x) => x.w)), h: b.reduce((t, x) => t + x.h, 0) + (b.length - 1) * U.between };
  };
  const beside = (cols: number, cell: Box): Box => {
    const b = list.map((a) => app(cols, cell, a));
    return { w: b.reduce((t, x) => t + x.w, 0) + (b.length - 1) * U.beside, h: Math.max(...b.map((x) => x.h)) };
  };
  return { rows: over(2, side), line: beside(2, side), strip: beside(2, under), cols: beside(1, side), stack: over(1, side) };
}

/** The AI usage widget's alert: below what percent left it warns. 20 until the user picks another, 0 is off. */
export const USAGE_ALERTS = [0, 10, 20, 30, 50] as const;
export function usageAlert(props: Record<string, unknown>): number {
  const v = Number(props.alertBelow ?? 20);
  return (USAGE_ALERTS as readonly number[]).includes(v) ? v : 20;
}

/**
 * What is left of each plan, as big as its tile allows: each app's rings side by side or one over the other, with
 * their words beside them or, along a wide and low tile, under them; the apps in rows or side by side. Whichever
 * lets the rings be biggest. Everything grows with the tile, rings and words alike, so a large tile is filled, not a
 * corner of it. Below 10px it scrolls down rather than shrink past reading, in the widest arrangement that fits across.
 */
export function usageFit(box: Box, apps: UsageApp[]): UsageFit {
  const b = usageBoxes(apps);
  const order: UsageFit["id"][] = ["rows", "line", "strip", "cols", "stack"];
  const best = biggest<UsageFit["id"]>(box, order.map((id) => ({ id, natural: b[id] })), 56);
  if (best.px >= 10 || !(box.w > 0)) return { ...best, px: Math.max(10, best.px) };
  const across = order.filter((id) => b[id].w * 10 <= box.w);
  const pick = across.length ? biggest<UsageFit["id"]>(box, across.map((id) => ({ id, natural: b[id] }))) : { id: "stack" as const, px: 10 };
  return { id: pick.id, px: 10 };
}

// ---------------------------------------------------------------------------
// Habits

/** The days a habit grid shows: whole weeks, Monday first, the last one this week. */
export function habitDays(today: Date, weeks: number): Date[] {
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7) - 7 * (weeks - 1));
  return Array.from({ length: weeks * 7 }, (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
}

/** Days in a row a habit was done, up to today, or up to yesterday while today is still open. */
export function habitStreak(log: Record<string, string[]>, id: string, today: Date): number {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (!(log[dayKey(d)] ?? []).includes(id)) d.setDate(d.getDate() - 1);
  let n = 0;
  while ((log[dayKey(d)] ?? []).includes(id) && n < 3660) {
    n++;
    d.setDate(d.getDate() - 1);
  }
  return n;
}

// a habit tracker's sizes in em, measured from the styles in widgets.css (Habits)
export const HB = { name: 8.8, cell: 1.62, gap: 0.36, week: 0.75, streak: 2.2, head: 2.25, row: 2.06, line: 1.59, foot: 2.6, todayW: 15, todayHead: 1.9, todayRow: 2.35 };

/** The names' column, in em, from the longest name: about half an em a letter at the names' size, with room after. */
/**
 * The habits as saved, each with an id and a name, whatever an agent sent: a
 * missing id comes from its name, so its ticks stay with it however the list is
 * reordered later, and a missing name from its title or label. Nothing an agent
 * sent is dropped, so the user's next edit saves it all.
 */
export function readHabits(raw: unknown): { id: string; name: string }[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw.flatMap((h, i) => {
    const o: Record<string, unknown> = typeof h === "string" ? { name: h } : h && typeof h === "object" ? (h as Record<string, unknown>) : {};
    const name = [o.name, o.title, o.label, o.text].find((x): x is string => typeof x === "string" && !!x.trim());
    let id = typeof o.id === "string" && o.id ? o.id : typeof o.id === "number" ? String(o.id) : "";
    if (!name && !id) return [];
    if (!id) id = "habit-" + ((name ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || String(i));
    // the same id twice: the second gets a number, in its order
    for (let n = 2, base = id; seen.has(id); n++) id = `${base}-${n}`;
    seen.add(id);
    return [{ ...o, id, name: name?.trim() ?? "Habit" }];
  });
}

export function habitNameWidth(names: string[]): number {
  const longest = names.reduce((m, n) => Math.max(m, n.length), 0);
  return Math.min(12, Math.max(5.5, longest * 0.5 + 0.6));
}

/** The days' columns of a habit grid, and the streaks' after them: their width in em. */
const daysWidth = (weeks: number) => weeks * 7 * HB.cell + (weeks - 1) * HB.week + (weeks * 7 - 1) * HB.gap + HB.gap + HB.streak;

/** Names beside the days: the grid's natural size in em. */
export function habitBox(weeks: number, rows: number, foot: boolean, name = HB.name): Box {
  return { w: name + HB.gap + daysWidth(weeks), h: HB.head + Math.max(1, rows) * HB.row + (foot ? HB.foot : 0) };
}

/** Each name over its days, for a tall tile: the grid's natural size in em. */
export function habitStackBox(weeks: number, rows: number, foot: boolean): Box {
  return { w: daysWidth(weeks), h: HB.head + Math.max(1, rows) * (HB.line + HB.row) + (foot ? HB.foot : 0) };
}

export type HabitFit = { layout: "grid" | "stack"; weeks: number; px: number } | { layout: "today"; head: boolean; foot: boolean; weeks: 0; px: number; shown: number };

/**
 * As many whole weeks as fit with boxes big enough to tap, or as fit with no
 * smaller boxes than one week gets (a short, wide tile has room across for
 * more), or else one week as big as it fits; null when a week will not fit.
 */
function weeksFor(box: Box, natural: (weeks: number) => Box): { weeks: number; px: number } | null {
  const one = fitType(box, natural(1), 30);
  if (one < 10.5) return null;
  for (const weeks of [8, 6, 4, 3, 2]) {
    const px = fitType(box, natural(weeks), 30);
    if (px >= 13 || px >= one * 0.97) return { weeks, px };
  }
  return { weeks: 1, px: one };
}

/**
 * How a habit tracker fills its tile: names beside their days, or each name
 * over its days in a tall tile, with as many whole weeks as fit, whichever
 * draws the bigger boxes (or, near enough the same, more weeks). A tile too
 * small for a week lists today's habits to tick, dropping its heading and add
 * button if it must, then the habits that do not fit, which it counts.
 */
export function habitFit(box: Box, rows: number, foot: boolean, name = HB.name): HabitFit {
  const grid = weeksFor(box, (w) => habitBox(w, rows, foot, name));
  const stack = weeksFor(box, (w) => habitStackBox(w, rows, foot));
  // bigger boxes win; when the two are within a step of each other, more weeks do
  const a = grid && { layout: "grid" as const, ...grid };
  const b = stack && { layout: "stack" as const, ...stack };
  const pick = a && b ? (Math.abs(a.px - b.px) <= 0.08 * Math.max(a.px, b.px) ? (b.weeks > a.weeks ? b : a) : b.px > a.px ? b : a) : (a ?? b);
  if (pick) return pick;
  // today's list: its heading and add button go first, then the habits that do not fit, which it counts
  const all = Math.max(1, rows);
  const today = (head: boolean, foot: boolean, shown: number): HabitFit => ({ layout: "today", head, foot, weeks: 0, shown, px: fitType(box, { w: HB.todayW, h: (head ? HB.todayHead : 0) + shown * HB.todayRow + (foot ? HB.foot : 0) }, 30) });
  for (const v of [today(true, foot, all), today(true, false, all), today(false, false, all)]) if (v.px >= 11) return v;
  for (let shown = all - 1; shown > 1; shown--) {
    const v = today(false, false, shown);
    if (v.px >= 10.5) return v;
  }
  return today(false, false, 1);
}

// ---------------------------------------------------------------------------
// Words: tasks, notes and links read at a reading size, the sticky note's handwriting fills it

// ---------------------------------------------------------------------------
// Connections

// Connections' sizes in em, measured from widgets.css (Connections): the count over what needs you and the line of
// what that is, or all of it on one line in a short tile
export const CONN = { full: { w: 13.2, h: 6.9 }, line: { w: 17, h: 1.35 } };

/** The Connections tile: the count big over what needs you, or one line when the tile is too short for that. */
export function connectionsFit(box: Box): { id: "full" | "line"; px: number } {
  return pickLayout<"full" | "line">(box, [{ id: "full", natural: CONN.full, min: 10.5 }, { id: "line", natural: CONN.line }], 22);
}

/** A reading size for words in a list or a note: it grows a little with the tile's width, then the tile shows more of them. */
export function readingType(w: number, base = 13, most = 16.5): number {
  return w > 0 ? Math.min(most, Math.max(base * 0.96, w / 24)) : base;
}

/**
 * The handwriting's size that fills a sticky note's box with its words: as
 * big as fits, from 14px to 72px. Caveat runs about 0.42em a letter, lines
 * are 1.18 tall, and wrapping loses about a sixth of each line; a word never
 * breaks, so the longest one must fit across.
 */
export function stickyType(box: Box, text: string): number {
  if (!(box.w > 0 && box.h > 0)) return 22;
  const words = text.trim() ? text : "Double-click to write";
  const paras = words.split("\n");
  const longest = words.split(/\s+/).reduce((m, x) => Math.max(m, x.length), 1);
  let lo = 10;
  let hi = 110;
  for (let i = 0; i < 20; i++) {
    const f = (lo + hi) / 2;
    const perLine = Math.max(1, Math.floor(box.w / (f * 0.42)));
    const lines = paras.reduce((n, para) => n + Math.max(1, Math.ceil((para.length * 1.17) / perLine)), 0);
    if (lines * f * 1.18 <= box.h && longest * f * 0.42 <= box.w) lo = f;
    else hi = f;
  }
  return Math.max(14, Math.min(72, Math.floor(lo)));
}

// ---------------------------------------------------------------------------
// Links

// a link's sizes in em, measured from the styles in widgets.css (Links): a tile like an app's icon, a row, or the icon alone
export const LINK = { tile: { w: 5.6, h: 4.7 }, row: { w: 13, h: 2.7 }, icon: { w: 2.4, h: 2.4 }, gap: 0.5, iconGap: 0.35 };

export type LinksFit = { id: "tiles" | "rows" | "icons"; cols: number; px: number };

/**
 * Links as tiles in as many columns as lets them be biggest, or as rows with their sites in a tall, narrow tile; a few
 * links get big icons. A tile too small for named tiles to read shows the sites' icons alone, named on hover.
 */
export function linksFit(box: Box, n: number): LinksFit {
  const named = namedLinksFit(box, n);
  if (named.px >= 11) return named;
  // past what fits at a size that reads, before the tile is measured or with more links than icons can show, they
  // keep that size and the list scrolls
  if (!(box.w > 0 && box.h > 0)) return { ...named, px: 11 };
  let icons: LinksFit = { id: "icons", cols: 1, px: -1 };
  for (let cols = 1; cols <= Math.max(1, n); cols++) {
    const px = fitType(box, gridBox(Math.max(1, n), cols, LINK.icon, LINK.iconGap), 16);
    if (px > icons.px + 1e-9) icons = { id: "icons", cols, px };
  }
  return icons.px >= 8 ? icons : { ...named, px: 11 };
}

function namedLinksFit(box: Box, n: number): LinksFit {
  const count = Math.max(1, n);
  const rows: LinksFit = { id: "rows", cols: 1, px: fitType(box, gridBox(count, 1, LINK.row, 0.3), 20) };
  if (box.w > 0 && box.h > 0 && box.w / box.h < 0.75 && rows.px >= 11) return rows;
  let tiles: LinksFit = { id: "tiles", cols: 1, px: -1 };
  for (let cols = 1; cols <= count; cols++) {
    const px = fitType(box, gridBox(count, cols, LINK.tile, LINK.gap), 20);
    if (px > tiles.px + 1e-9) tiles = { id: "tiles", cols, px };
  }
  // icons, unless a list reads clearly bigger
  return rows.px > tiles.px * 1.15 ? rows : tiles;
}

// ---------------------------------------------------------------------------
// Calendar

// a month's sizes in em, measured from the styles in widgets.css (Calendar): the month, and the next events beside or under it
export const MONTH = { w: 17.5, h: 16.6, side: 12.5, below: 7.4, next: 2.5 };

/** next: a month alone, with events coming, keeps a line under it for the next one, so a calendar that has them shows it */
export type CalendarFit = { id: "month" | "side" | "below"; px: number; titles: boolean; next?: boolean };

/**
 * The month as big as the tile allows, with the next events beside it in a
 * wide tile or under it in a tall one when there are any; the days stretch to
 * fill the tile, and big ones show their events' names.
 */
export function calendarFit(box: Box, events: boolean): CalendarFit {
  const ways: Layout<CalendarFit["id"]>[] = [{ id: "month", natural: { w: MONTH.w, h: MONTH.h } }];
  if (events) ways.push({ id: "side", natural: { w: MONTH.w + MONTH.side, h: MONTH.h } }, { id: "below", natural: { w: MONTH.w, h: MONTH.h + MONTH.below } });
  // the next events are worth more than a bigger month: they take their room whenever the month still reads well
  const month = fitType(box, ways[0].natural, 22);
  const others = events ? biggest(box, ways.slice(1), 22) : null;
  // with no room for the list, the next event still shows: on a line under the month, while the month reads
  const withNext = events ? fitType(box, { w: MONTH.w, h: MONTH.h + MONTH.next }, 22) : 0;
  const pick = others && others.px >= 12.5 ? others : withNext >= 9 ? { id: "month" as const, px: withNext, next: true } : { id: "month" as const, px: month };
  // a day both tall and wide enough has room for its events' names; a narrow one would cut them to a letter
  const rows = pick.id === "below" ? box.h - MONTH.below * pick.px : box.h;
  const cols = pick.id === "side" ? box.w - MONTH.side * pick.px : box.w;
  const tall = (rows - 4 * pick.px) / 6;
  const wide = cols / 7;
  return { ...pick, titles: tall >= 3.4 * pick.px && wide >= 4.6 * pick.px };
}

// ---------------------------------------------------------------------------
// Numbers: the number, the chart and the goal

/** How wide a number is, in em at its own size: figures, separators and signs each their own width. */
export function numberWidth(s: string): number {
  let w = 0;
  for (const ch of s) w += /[0-9$€£¥#]/.test(ch) ? 0.6 : /[,.:']/.test(ch) ? 0.28 : ch === "%" ? 0.85 : ch === " " ? 0.25 : /[−\-+]/.test(ch) ? 0.5 : 0.62;
  return Math.max(1, w);
}

// a number's sizes in em, measured from the styles in widgets.css (Number): its figure, its change (beside it, or
// under it with a gap), the least room its chart takes under it or beside it, and the space between them; and the
// space between the change and the now count beside it
export const METRIC = { value: 2.9, chip: 1.75, gap: 0.55, beside: 0.7, plot: 3.6, plotW: 10, above: 0.7, live: 0.5 };

export type MetricFit = {
  id: "stack" | "side" | "bare";
  px: number;
  /** the head's height in em: the figure, with its change under it unless inline */
  head: number;
  /** the change sits beside the figure, on its line */
  inline: boolean;
  /** the change shows its figure alone, its words in its tooltip */
  short: boolean;
};

/** The figure's, the change's and the now count's widths in em of the widget's type, as the page drew them (Metric's ruler). */
export type MetricWidths = { value: number; full: number; short: number; live?: number };

/** The em width of a change chip that says text: about 0.43em a character at its size, with its arrow and padding. */
const chipW = (text: string) => text.length * 0.43 + 1.6;

/** The head's width in em: the figure, and beside it or under it the change and the now count, side by side. */
export function metricHeadW(f: Pick<MetricFit, "inline" | "short">, m: MetricWidths): number {
  const change = f.short ? m.short : m.full;
  const aside = change && m.live ? change + METRIC.live + m.live : change || (m.live ?? 0);
  return f.inline ? m.value + (aside ? METRIC.beside + aside : 0) : Math.max(m.value, aside);
}

/**
 * The number, its change and its chart, sized to fill the tile: the figure with its change beside it or under it,
 * over a chart a third taller than they are; or, in a short wide tile, the chart beside them. The change gives up its
 * words (they stay in its tooltip) only when keeping them would shrink the figure by nearly a third. A now count
 * (Google Analytics' active users this minute) sits beside the change, on its line. A number with no history to
 * draw is the figure and its change alone.
 */
export function metricFit(box: Box, value: string, chip: { delta: string; label: string } | null, history: boolean, measured?: MetricWidths | null, live: string | null = null): MetricFit {
  // the widths as the page drew them, once it has; until then, as the type is known to run
  const vw = measured?.value ?? numberWidth(value) * METRIC.value;
  const full = chip ? (measured?.full ?? chipW([chip.delta, chip.label].filter(Boolean).join(" "))) : 0;
  const short = chip?.delta ? (measured?.short ?? chipW(chip.delta)) : full;
  const m: MetricWidths = { value: vw, full, short, live: live ? measured?.live || chipW(live) : 0 };
  type Head = { w: number; h: number; inline: boolean; short: boolean };
  const under = METRIC.value + METRIC.gap + METRIC.chip;
  const head = (inline: boolean, short: boolean): Head => ({ w: metricHeadW({ inline, short }, m) + (inline ? 0.3 : 0), h: inline ? METRIC.value : under, inline, short });
  const heads: Head[] = chip || live
    ? [
        head(true, false),
        head(false, false),
        // with the now count beside it, the change may give up its words under the figure too, in a narrow tile
        ...(short < full ? [head(true, true), ...(live ? [head(false, true)] : [])] : []),
      ]
    : [head(true, false)];
  const MAX = history ? 34 : 40;
  let best: MetricFit | null = null;
  let bestScore = -1;
  for (const head of heads) {
    // the words of the change are worth a fifth of the figure's size; the chart under it is worth a fifth over beside
    // and a change on the figure's line leaves its chart more room than one under it
    const worth = (head.short ? 0.7 : 1) * (head.inline ? 1 : 0.85);
    const ways: { id: "stack" | "side" | "bare"; natural: Box; worth: number }[] = history
      ? [
          { id: "stack", natural: { w: head.w + 0.2, h: head.h + METRIC.above + Math.max(METRIC.plot, 1.3 * head.h) }, worth },
          { id: "side", natural: { w: head.w + 1.4 + METRIC.plotW, h: head.h + 0.4 }, worth: worth * 0.8 },
        ]
      : [{ id: "bare", natural: { w: head.w + 0.3, h: head.h + 0.3 }, worth }];
    for (const way of ways) {
      const px = fitType(box, way.natural, MAX);
      const score = px * way.worth;
      if (score > bestScore + 1e-9) {
        bestScore = score;
        best = { id: way.id, px, head: head.h, inline: head.inline, short: head.short };
      }
    }
  }
  const fit = best!;
  // a long word in place of a number never shrinks the words with it past reading: the number clips instead
  if (fit.id === "bare") return { ...fit, px: Math.max(11, fit.px) };
  // too small for a chart to read: the figure and its change alone, in whichever head draws them biggest (in the
  // Number's least tile, $1.30M over its change, where the change beside it would cut the figure)
  if (fit.px < 11) {
    const alone = heads.map((h) => ({ h, px: fitType(box, { w: h.w + 0.3, h: h.h + 0.3 }, 40) })).reduce((a, b) => (b.px >= a.px ? b : a));
    return { id: "bare", px: Math.max(11, alone.px), head: alone.h.h, inline: alone.h.inline, short: alone.h.short };
  }
  return fit;
}

/** A chart's type size: its number over the plot grows with the tile, and the plot takes the rest. */
export function chartType(box: Box): number {
  if (!(box.w > 0 && box.h > 0)) return 13;
  return Math.max(11, Math.min(22, box.w / 24, box.h / 10));
}

// a goal's sizes in em, measured from the styles in widgets.css (Goal): the ring and its figures, and each line of
// small words (when it ends, its pace) under it, beside it or under the bar
export const GOAL = { ring: { w: 9.4, h: 8.9 }, side: { w: 16.8, h: 6.9 }, bar: { w: 13.5, h: 4.7 }, line: 1.15, ch: 0.43 };

export type GoalFit = { id: "ring" | "side" | "bar"; px: number };

/**
 * A goal as a ring with its words under it, or beside it in a wide tile; or as
 * a bar, as wide as its words. Its small lines (when it ends, its pace) take
 * their room: under the ring each is a line of its own, as wide as it reads;
 * under a bar they share one.
 */
export function goalFit(box: Box, style: "ring" | "bar", value = "$10,000", of = "of $10,000", lines: string[] = []): GoalFit {
  const said = lines.filter(Boolean);
  const longest = said.reduce((m, l) => Math.max(m, l.length * GOAL.ch), 0);
  if (style === "bar") {
    const w = Math.max(GOAL.bar.w, numberWidth(value) * 2.5 + of.length * 0.42 + 3.6, said.join(" · ").length * GOAL.ch);
    return { id: "bar", px: Math.max(10, fitType(box, { w, h: GOAL.bar.h + (said.length ? GOAL.line : 0) }, 30)) };
  }
  const ring = { w: Math.max(GOAL.ring.w, longest + 0.6), h: GOAL.ring.h + said.length * GOAL.line };
  const side = { w: Math.max(GOAL.side.w, 7.5 + Math.max(longest, numberWidth(value) * 2.1, of.length * 0.45)), h: Math.max(GOAL.side.h, 3.6 + said.length * GOAL.line) };
  const best = biggest<"ring" | "side">(box, [{ id: "ring", natural: ring }, { id: "side", natural: side }], 34);
  return { ...best, px: Math.max(9, best.px) };
}

// ---------------------------------------------------------------------------
// ContextBolt SEO and Bookmarks (src/widgets/ours.tsx, src/styles/ours.css)

export type OursFitView = "week" | "wins" | "ai" | "keywords" | "research" | "credits" | "recent" | "lately" | "topics" | "search";
export interface OursFit {
  /** full: everything; chart: the numbers and their chart; stat: the number alone, with its bars; bare: the number; narrow: a list's one column */
  id: "full" | "chart" | "stat" | "bare" | "narrow";
  px: number;
  /** how many rows of a part with rows to show (queries, rivals) */
  rows: number;
  /** whether the extra part fits: SEO's own sentence about the week, or the pages AI answers cite */
  insight: boolean;
}

// Each view's parts in em, measured from ours.css: the hero line (its big number and the words beside it), a row of
// three small numbers, a day's bars and their dates, a row of a list, a part's small label, the line at the foot,
// and the gap between parts
export const OURS_EM = { hero: 3.3, stats: 3.6, bars: 5.6, axis: 1.3, row: 1.95, kicker: 1.5, foot: 1.7, gap: 0.75, insight: 2.9, listRow: 2.7, listHead: 1.6 };

/**
 * A view of ContextBolt SEO or Bookmarks, as big as its tile allows: the richest of its layouts that still reads at
 * 11px, its type grown to fill the tile up to a most; a list's type set by the tile's width, its rows scrolling.
 */
export function oursFit(view: OursFitView, box: Box, o: { queries?: number; insight?: boolean; bars?: boolean; rivals?: number; check?: boolean; rows?: number }): OursFit {
  const O = OURS_EM;
  const w = box.w > 0 ? box.w : 400;
  const h = box.h > 0 ? box.h : 260;
  const list = (narrowAt: number): OursFit => {
    const px = readingType(w, 12.5, 16);
    return { id: w / px < narrowAt ? "narrow" : "full", px, rows: o.rows ?? 0, insight: false };
  };
  const pick = (layouts: { id: OursFit["id"]; natural: Box; min?: number }[], most: number, rows = 0, insight = false): OursFit => {
    const l = pickLayout({ w, h }, layouts.map((x) => ({ ...x, min: x.min ?? 11 })), most);
    return { id: l.id, px: Math.max(10, l.px), rows, insight };
  };
  switch (view) {
    case "week": {
      const q = Math.min(4, o.queries ?? 0);
      const bars = o.bars === false ? 0 : O.bars + O.axis + O.gap;
      const chart = O.hero + O.gap + O.stats + O.gap + bars + O.foot;
      const queries = q ? O.kicker + q * O.row + O.gap : 0;
      const withInsight = chart + queries + (o.insight ? O.insight : 0);
      const layouts: { id: OursFit["id"]; natural: Box; min?: number }[] = [
        ...(o.insight ? [{ id: "full" as const, natural: { w: 26, h: withInsight } }] : []),
        ...(q ? [{ id: "full" as const, natural: { w: 26, h: chart + queries } }] : []),
        { id: "chart", natural: { w: 22, h: chart } },
        { id: "stat", natural: { w: 12.5, h: O.hero + (o.bars === false ? 0 : O.gap + 3) } },
      ];
      const l = pick(layouts, 17, q);
      // the first full layout is the one with SEO's sentence: it fits when that one was picked
      return { ...l, insight: !!o.insight && l.id === "full" && fitType({ w, h }, layouts[0].natural) >= 11 };
    }
    case "ai":
      if (o.check) return pick([{ id: "chart", natural: { w: 16, h: O.hero + 1.6 + O.foot + O.gap } }, { id: "stat", natural: { w: 11, h: O.hero } }], 18);
      {
        const rivals = Math.min(6, o.rivals ?? 0);
        // the share and its rank, a line of facts; the rivals beside them on a wide tile, under them otherwise
        const main = O.hero + O.gap + 1.9 + O.gap + 4.6;
        const fixed = O.hero + O.gap + 1.9 + O.gap + 1.5 + O.gap + O.kicker + O.gap + O.foot;
        // a tall wide tile lists the pages AI answers cite under the facts (o.rows of them), when they read at 13px
        const pages = Math.min(4, o.rows ?? 0);
        const sovH = O.kicker + Math.max(3, rivals) * O.row;
        const layouts: { id: OursFit["id"]; natural: Box; min?: number }[] = [
          ...(pages ? [{ id: "full" as const, natural: { w: 34, h: Math.max(main + O.gap + O.kicker + pages * O.row, sovH) + O.gap + O.foot }, min: 13 }] : []),
          { id: "full", natural: { w: 34, h: Math.max(main, sovH) + O.gap + O.foot } },
          { id: "chart", natural: { w: 21, h: fixed + Math.min(3, rivals) * O.row } },
          { id: "stat", natural: { w: 11, h: O.hero + O.gap + 1.9 } },
        ];
        const l = pick(layouts, 18);
        const withPages = !!pages && l.id === "full" && fitType({ w, h }, layouts[0].natural, 18) >= 13;
        // under the share, as many rivals as the tile has room for, at least three
        const room = l.id === "chart" ? Math.floor((h / l.px - fixed) / O.row) : rivals;
        return { ...l, rows: Math.min(rivals, Math.max(3, room)), insight: withPages };
      }
    case "credits":
      return pick(
        [
          { id: "full", natural: { w: 16, h: O.hero + O.gap + 0.7 + O.gap + 3.3 } },
          { id: "chart", natural: { w: 14, h: O.hero + O.gap + 0.7 + O.gap + 3.3 } },
          { id: "bare", natural: { w: 10, h: O.hero + 1.7 } },
        ],
        22,
      );
    case "lately":
      return pick(
        [
          { id: "full", natural: { w: 22, h: O.hero + O.gap + O.bars + O.axis + O.gap + 1.8 + O.gap + O.foot } },
          { id: "chart", natural: { w: 20, h: O.hero + O.gap + O.bars + O.axis + O.gap + O.foot } },
          { id: "stat", natural: { w: 13, h: O.hero + O.gap + 3 } },
          { id: "bare", natural: { w: 11, h: O.hero } },
        ],
        18,
      );
    case "wins":
    case "keywords":
      return list(23);
    case "research":
    case "recent":
      return list(20);
    case "topics":
      return list(26);
    case "search":
      return list(18);
  }
}

// ---------------------------------------------------------------------------
// Breakdowns: parts of a whole (Split), a ranked list with its columns (Ranked), and Search performance

// a split's sizes in em, measured from the styles in widgets.css (Split): its ring, the gap to its legend, a legend
// row and how wide one reads, the bar and the gaps around it, and the share and name over the bar
export const SPLIT = { ring: 7.4, gap: 1.3, row: 1.95, legend: 12.6, bar: 0.8, barGap: 0.85, head: 2.75 };

export type SplitFit = { id: "ring" | "bar"; px: number; rows: number; cols: number };

/**
 * Parts of a whole: a ring with its legend beside it, a row a part, the smallest added up as Other when the tile is too
 * short for every one; or, in a tile too short or narrow for a ring, the biggest part's share over one bar with the
 * legend under it in one or two columns, as many rows as fit at a reading size (every part is in the bar).
 */
export function splitFit(box: Box, parts: number): SplitFit {
  const n = Math.max(1, parts);
  // a ring beside its legend is about three times as wide as it is tall: a much wider tile reads better as a bar
  if (box.w <= box.h * 3.4)
    for (let k = n; k >= Math.min(n, 3); k--) {
      const ring = fitType(box, { w: SPLIT.ring + SPLIT.gap + SPLIT.legend, h: Math.max(SPLIT.ring, k * SPLIT.row) + 0.2 }, 22);
      if (ring >= 11.5) return { id: "ring", px: ring, rows: k, cols: 1 };
    }
  // big enough for two legend rows under the bar, where the tile is tall enough
  const px = Math.max(11, Math.min(16, box.w / 24, box.h / (SPLIT.head + SPLIT.bar + 2 * SPLIT.barGap + 2 * SPLIT.row)));
  const cols = box.w / px >= 2 * SPLIT.legend + SPLIT.gap ? 2 : 1;
  const room = Math.floor((box.h / px - SPLIT.head - SPLIT.bar - SPLIT.barGap * 2 + 0.3) / SPLIT.row);
  return { id: "bar", px, rows: Math.max(0, Math.min(Math.ceil(n / cols), room)) * cols, cols };
}

// a ranked list's sizes in em (widgets.css, Ranked): its rank, its value, a label's least width, an extra column, the gaps
export const RANKED = { rank: 1.5, value: 4.4, label: 9.5, column: 5.4, gap: 0.7, row: 2.5, head: 1.7 };

/** A ranked list's type size, and how many of its extra columns fit beside the label at it: the first ones given go first. */
export function rankedFit(box: Box, extras: number): { px: number; columns: number } {
  const px = readingType(box.w, 13, 16);
  const room = box.w / px - RANKED.rank - RANKED.value - RANKED.label - RANKED.gap * 3;
  return { px, columns: Math.max(0, Math.min(extras, Math.floor(room / (RANKED.column + RANKED.gap)))) };
}

// Search performance's sizes in em (widgets.css, Search performance): a number's cell, and the least room its chart needs
export const PERF = { cell: { w: 6.6, h: 5.5 }, gap: 0.7, chart: 6.5 };

export type PerfFit = { id: "row" | "grid"; px: number };

/** The four numbers in a row over their chart; or, in a small tile, the four numbers alone in two rows of two. */
export function perfFit(box: Box): PerfFit {
  const row = { w: 4 * PERF.cell.w + 3 * PERF.gap, h: PERF.cell.h + PERF.gap + PERF.chart };
  const px = Math.min(17, box.w / row.w);
  if (px >= 11 && box.h / px >= row.h) return { id: "row", px };
  return { id: "grid", px: Math.max(10, fitType(box, gridBox(4, 2, PERF.cell, PERF.gap), 22)) };
}
